import { Controller } from "react-hook-form"
import { useState, useRef } from "react"
import useOnClickOutside from "@/lib/hooks/useOnClickOutside"
import Checkmark from "@/common/icons/checkmark"
import { Caret } from "@/common/icons/caret"
import Plus from "@/components/common/icons/plus"
import Minus from "@/components/common/icons/minus"
import Close from "@/common/icons/close"
import classNames from "classnames"
import styles from "./filter-control.module.css"

export const FilterControl = ({
  className,
  control,
  hierarchical = false,
  isOpen,
  label,
  name,
  onReset,
  onSelect,
  options,
  selected = [],
  setOpen,
  showCount = false,
  hideEmpty = false,
  termCounts = [],
  strings = { all: "Alla", singular: "vald", plural: "valda" }
}) => {
  const elemRef = useRef(null)
  const [openParents, setOpenParents] = useState([])
  useOnClickOutside(elemRef, () => setOpen(name), isOpen)

  const checkCount = selected.length
  const currentChoice =
    checkCount === 0
      ? strings?.all
      : checkCount +
        (checkCount === 1 ? " " + strings?.singular : " " + strings?.plural)

  const selectedParents = getSelectedParents(selected, options)
  const isEmpty = hideEmpty && termCounts.every((item) => item.count === 0)

  return (
    <Controller
      {...{ control, name }}
      render={({ field }) => {
        const hasValues = field.value && field.value.length > 0
        return (
          <div
            className={classNames(styles.controller, className, {
              "has-value": hasValues,
              "is-open": isOpen,
              "is-hierarchical": hierarchical,
              "is-empty": isEmpty
            })}
            ref={elemRef}>
            <div
              className={classNames({
                checkboxes: true,
                "has-shadow": isOpen
              })}>
              <header onClick={() => !isEmpty && setOpen(name)}>
                <div className="current-choice">
                  <span>{label}</span>
                  {currentChoice}
                </div>
                {hasValues && (
                  <div
                    className="clear-filter"
                    onClick={(e) => {
                      e.stopPropagation()
                      onReset(name)
                    }}>
                    <Close />
                  </div>
                )}
                <Caret />
              </header>
              {isOpen && (
                <div className="open-filter">
                  <div className="overlay" />
                  <ul>
                    {options.length > 0 &&
                      options.map((option) => {
                        if (
                          hierarchical &&
                          option?.parent > 0 &&
                          !openParents.includes(option.parent)
                        ) {
                          return null
                        }

                        return (
                          <Checkbox
                            key={option.value}
                            isParent={hierarchical && option?.parent === 0}
                            isChild={hierarchical && option?.parent > 0}
                            isOpen={
                              hierarchical && openParents.includes(option.value)
                            }
                            hasSelectedChildren={
                              hierarchical &&
                              selectedParents.includes(option.value)
                            }
                            setOpen={setOpenParents}
                            showCount={showCount}
                            hideEmpty={hideEmpty}
                            termCounts={termCounts}
                            name={name}
                            isChecked={selected.includes(option.value)}
                            {...{ option, onSelect }}
                          />
                        )
                      })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )
      }}
    />
  )
}

const Checkbox = ({
  option,
  name,
  isParent,
  isChild,
  isOpen,
  setOpen,
  hasSelectedChildren,
  isChecked,
  onSelect,
  showCount,
  hideEmpty,
  termCounts
}) => {
  const data = termCounts.find((item) => item.slug === option.value)
  const count = data?.count === undefined ? option.count : data.count

  if (!isChecked && hideEmpty && count === 0) {
    return null
  }

  return (
    <li
      onClick={() => {
        onSelect(name, option.value)
        isParent && !isChecked && setOpen([option.value])
      }}
      className={classNames("checkbox", {
        "is-checked": isChecked,
        "is-child": isChild,
        "is-parent": isParent,
        "has-checked-children": hasSelectedChildren
      })}>
      <div className="square">
        {isChecked && <Checkmark className="w-3" fillColor="#101010" />}
        {hasSelectedChildren && !isChecked && <div className="parent-dot" />}
      </div>
      <div className="label-wrapper">
        <span className="label">{option.label}</span>
        {showCount && <span className="count">{count}</span>}
        {isParent && !isOpen && (
          <div
            className="icon-wrapper"
            onClick={(e) => {
              e.stopPropagation()
              setOpen([option.value])
            }}>
            <Plus className="w-3" />
          </div>
        )}
        {isParent && isOpen && (
          <div
            className="icon-wrapper"
            onClick={(e) => {
              e.stopPropagation()
              setOpen([])
            }}>
            <Minus className="w-3" />
          </div>
        )}
      </div>
    </li>
  )
}

const getSelectedParents = (selected, options) => {
  return selected.map((item) => {
    const option = options.find((option) => option.value === item)

    return option?.parent
  })
}
